import React, { useState, useRef } from 'react';
import {
  KTSVG,
  SimpleAlert,
  maskPhoneNumber,
  toAbsoluteUrl,
} from '../../../../_mexablock/helpers';
import { Modal } from 'react-bootstrap-v5';
import { NumericFormat } from 'react-number-format';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import Select from 'react-select';
import { addEmpresas } from '../../../service/empresas/Empresas';
import { useGetEmpSocio } from '../hooks/EmpresasHook';
import {
  useSelectGeneric,
  useSelectTypePerson,
} from '../../catalogos/generico/hooks/genericoHook';

type Props = {
  show: boolean;
  handleClose: (updateGrid: boolean) => void;
  title: string;
  id_row: number;
};

const EmpresaSchema = Yup.object().shape({
  type_id: Yup.number().min(1, 'Requerido.').required('Requerido.'),
  type_contributor_id: Yup.number().required('Requerido.').min(1, 'Requerido.'),
  type_commission_id: Yup.number().required('Requerido.').min(1, 'Requerido.'),
  street: Yup.string()
    .max(30, 'Máximo 30 caracteres')
    .when('type_contributor_id', {
      is: 2,
      then: (schema) => schema.required('Requerido.'),
      otherwise: (schema) => schema.notRequired(),
    }),
  city: Yup.string()
    .max(30, 'Máximo 30 caracteres')
    .when('type_contributor_id', {
      is: 2,
      then: (schema) => schema.required('Requerido.'),
      otherwise: (schema) => schema.notRequired(),
    }),
  state: Yup.string()
    .max(30, 'Máximo 30 caracteres')
    .when('type_contributor_id', {
      is: 2,
      then: (schema) => schema.required('Requerido.'),
      otherwise: (schema) => schema.notRequired(),
    }),
  postal_code: Yup.string().when(['type_contributor_id', 'type_id'], {
    is: (type_contributor_id: any, type_id: any) =>
      Number(type_contributor_id) === 2 || Number(type_id) === 12,
    then: (schema) =>
      schema.required('Requerido.').min(5, 'Requerido.').max(5, 'Requerido.'),
    otherwise: (schema) =>
      schema
        .notRequired()
        .nullable()
        .transform((v) => (v == null ? '' : v)),
  }),
  name: Yup.string()
    .max(100, 'Máximo 100 caracteres')
    .when('type_contributor_id', {
      is: 2,
      then: (schema) => schema.required('Requerido.'),
      otherwise: (schema) => schema.notRequired(),
    }),
  business_name: Yup.string()
    .max(100, 'Máximo 100 caracteres')
    .when('type_contributor_id', {
      is: 2,
      then: (schema) => schema.required('Requerido.'),
      otherwise: (schema) => schema.notRequired(),
    }),
  email: Yup.string()
    .email('Formato de correo inválido.')
    .min(3, 'Mínimo 3 caracteres.')
    .max(50, 'Máximo 50 caracteres.')
    .required('Requerido.'),
  billing_mail: Yup.string()
    .email('Formato de correo inválido.')
    .min(3, 'Mínimo 3 caracteres.')
    .max(50, 'Máximo 50 caracteres.')
    .required('Requerido.'),
  cfdi_id: Yup.number().required('Requerido.').min(1, 'Requerido.'),
  pdfFile: Yup.mixed<File>()
    .required('Requerido.')
    .test('is-pdf', 'El archivo debe ser un PDF', (value) => {
      if (value) {
        return value.type === 'application/pdf';
      }
      return false;
    }),
  rfc: Yup.string()
    .required('Requerido.')
    .matches(
      /^([A-ZÑ&]{3,4}) ?(?:- ?)?(\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])) ?(?:- ?)?([A-Z\d]{2})([A\d])$/,
      'Formato de RFC inválido'
    ),
  interbank_key: Yup.string()
    .min(18, 'Mínimo 18 caracteres')
    .required('Requerido.'),
  first_name: Yup.string()
    .max(30, 'Máximo 30 caracteres')
    .required('Requerido.'),
  mother_last_name: Yup.string().max(30, 'Máximo 30 caracteres'),
  father_last_name: Yup.string()
    .max(30, 'Máximo 30 caracteres')
    .required('Requerido.'),
  birthdate: Yup.date().required('Requerido.'),
  phone: Yup.string().required('Requerido.').min(10, 'Formato inválido'),
  gender_id: Yup.number().required('Requerido.').min(1, 'Requerido.'),
  account_owner_first_name: Yup.string()
    .max(30, 'Máximo 30 caracteres')
    .required('Requerido.'),
  account_owner_last_name: Yup.string()
    .max(30, 'Máximo 30 caracteres')
    .required('Requerido.'),
  bank_name: Yup.string()
    .max(30, 'Máximo 30 caracteres')
    .required('Requerido.'),
  commission_percent: Yup.number()
    .max(100, 'El porcentaje no puede ser mayor que 100')
    .min(1, 'El porcentaje no puede ser menor que 1')
    .required('Requerido.'),
});

const currentDate = new Date();
const fechaMaxima = new Date(currentDate);
fechaMaxima.setFullYear(currentDate.getFullYear() - 18); // Restar 18 años
const maxDate = fechaMaxima.toISOString().split('T')[0];

const CreateModal: React.FC<Props> = ({ id_row, show, handleClose, title }) => {
  const [loading, setLoading] = useState(false);
  const { initValuesEmpSocio } = useGetEmpSocio();
  const constancyRef = useRef<HTMLInputElement>(null);
  const [documentName, setDocumentName] = useState<string>('');
  const { catalog: genders } = useSelectGeneric('GEN');
  const { catalog: types } = useSelectGeneric('TEMP');
  const { catalog: cfdi } = useSelectGeneric('CFDI');
  const { catalog: tipo_comision } = useSelectGeneric('TDC');
  const { catalogTypePerson: tipo_persona } = useSelectTypePerson();

  const [selectedType, setSelectedType] = useState<any>(null);
  const [selectedRegimen, setSelectedRegimen] = useState<any>(null);
  const [selectedComision, setSelectedComision] = useState<any>(null);
  const [selectedGender, setSelectedGender] = useState<any>(null);
  const [selectedCfdi, setSelectedCfdi] = useState<any>(null);

const filteredTypes = React.useMemo(() => {
  return (types ?? []).filter((item) => item.code !== 'DIST' && item.id !== 55);
}, [types]);

  React.useEffect(() => {
  if (show) {
    const defaultType = filteredTypes.find((item) => item.id === 13);
    if (defaultType) {
      setSelectedType({ value: defaultType.id, label: defaultType.name });
    }

    const defaultRegimen = tipo_persona.find((item) => item.id === 2);
    if (defaultRegimen) {
      setSelectedRegimen({
        value: defaultRegimen.id,
        label: defaultRegimen.name,
      });
    }
  }
}, [show, filteredTypes, tipo_persona]);

  const formEmpresa = useFormik({
    initialValues: initValuesEmpSocio,
    validationSchema: EmpresaSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      addEmpresas(values)
        .then((res) => {
          const {
            data: { message },
          } = res;
          SimpleAlert(message, 3000, 'success');
          resetFormState();
          setLoading(false);
          handleClose(true);
        })
        .catch((err) => {
          let resMessageToast: string = '';
          const {
            data: { message, details = null },
          } = err.response;
          resMessageToast = `${message}`;
          if (details?.length) {
            setStatus(`${details[0]?.message}`);
          }
          setLoading(false);
          setSubmitting(false);
          SimpleAlert(resMessageToast, 3000, 'error');
        });
    },
  });

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const maskedPhoneNumber = maskPhoneNumber(value);
    formEmpresa.setFieldValue('phone', maskedPhoneNumber);
  };

  const onOpenFile = () => {
    constancyRef?.current?.click();
  };

  const handleChangeInput = (
    event: React.ChangeEvent<HTMLInputElement>,
    key: string
  ) => {
    const value = event.target.value;
    const valueReg = value.replace(/\d/g, '');
    const cleanInput = valueReg.replace(/^\s+|\s+(?=\s)/g, '');
    const inputLimpio = cleanInput.replace(/[^\wáéíóúüÁÉÍÓÚÜñÑ\s]/g, '');
    formEmpresa.setFieldValue(key, inputLimpio);
  };

  const changeUtilityValue = (values: { floatValue?: number }) => {
    const utilidad_porcentaje = values.floatValue || 0;
    formEmpresa.setFieldValue('commission_percent', utilidad_porcentaje);
  };

  const resetFormState = () => {
    formEmpresa.resetForm();
    setDocumentName('');
    setSelectedType(null);
    setSelectedRegimen(null);
    setSelectedComision(null);
    setSelectedGender(null);
    setSelectedCfdi(null);
  };

  return (
    <Modal
      id="kt_modal_create"
      tabIndex={-1}
      aria-hidden="true"
      dialogClassName="modal-dialog modal-dialog-centered modal-xl"
      show={show}
      onHide={() => {
        resetFormState();
        handleClose(false);
      }}
    >
      <div className="modal-content">
        <div className="modal-header pb-0 border-0 justify-content-end">
          <div
            className="btn btn-sm btn-icon btn-active-color-primary"
            data-bs-dismiss="modal"
            onClick={() => {
              resetFormState();
              handleClose(false);
            }}
          >
            <KTSVG
              className="svg-icon-2"
              path="/media/icons/duotune/arrows/arr061.svg"
            />
          </div>
        </div>

        <div className="modal-body scroll-y mx-5 pt-0">
          <div className="text-center mb-13">
            <h1 className="mb-3">{title} socio / empresa.</h1>
            <div className="text-muted fw-semibold fs-5">
              Agrega la información de socio / empresa.
            </div>
          </div>
          <form
            id="kt_modal_generic_form"
            className="form"
            action="#"
            onSubmit={formEmpresa.handleSubmit}
          >
            {/* inicio socio */}
            <div className="row g-9 mb-5">
              <div className="text-gray-700 mt-7 fs-4 fw-bold">
                Datos de identificación
              </div>
              <div className="separator my-5"></div>
              <div className="col-md-3 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className=" fw-bold fs-6 mb-2 w-auto required">
                    Tipo
                  </label>
                </div>
                <Select
                  options={filteredTypes.map((item) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                  placeholder="Seleccione"
                  onChange={(option: any) => {
                    const value = option ? Number(option.value) : 0;
                    setSelectedType(option);
                    formEmpresa.setFieldValue('type_id', value);

                    if (value === 12) {
                      formEmpresa.setFieldValue('type_contributor_id', 2);
                    } else if (value === 13) {
                      formEmpresa.setFieldValue('type_contributor_id', 2);
                      const defaultRegimen = tipo_persona.find(
                        (item) => item.id === 2
                      );
                      if (defaultRegimen) {
                        setSelectedRegimen({
                          value: defaultRegimen.id,
                          label: defaultRegimen.name,
                        });
                      }
                    } else {
                      formEmpresa.setFieldValue('type_contributor_id', 0);
                      setSelectedRegimen(null);
                    }

                    formEmpresa.setFieldValue('name', '');
                    formEmpresa.setFieldValue('business_name', '');
                    formEmpresa.setFieldValue('commission_percent', '');
                    formEmpresa.setFieldValue('street', '');
                    formEmpresa.setFieldValue('city', '');
                    formEmpresa.setFieldValue('state', '');
                    formEmpresa.setFieldValue('type_commission_id', 0);
                    formEmpresa.setFieldValue('postal_code', '');
                    setSelectedComision(null);
                  }}
                  value={selectedType}
                  isClearable
                  noOptionsMessage={() => <span>No hay registros</span>}
                  styles={{
                    placeholder: (provided) => ({
                      ...provided,
                      color: '#181c32',
                    }),
                    indicatorSeparator: (provided) => ({
                      ...provided,
                      display: 'none',
                    }),
                    dropdownIndicator: (provided) => ({
                      ...provided,
                      color: '#7E8199',
                    }),
                    control: (provided, state) => ({
                      ...provided,
                      border: '1px solid #e4e6ef',
                      borderRadius: '6px',
                      padding: '3px',
                      fontSize: '1.1rem',
                      boxShadow: 'none',
                      backgroundColor: state.isDisabled ? '#EFF2F5' : 'white',
                    }),
                    option: (provided) => ({
                      ...provided,
                      fontSize: '1.1rem',
                    }),
                  }}
                />
                {formEmpresa.touched.type_id && formEmpresa.errors.type_id && (
                  <div className="fv-plugins-message-container text-danger w-100 ms-5">
                    <span role="alert">{formEmpresa.errors.type_id}</span>
                  </div>
                )}
              </div>

              {formEmpresa.values.type_id === 13 ? (
                <div className="col-md-3">
                  <div className="w-100 d-flex">
                    <label className="required fw-bold fs-6 mb-2 w-auto">
                      Regimen
                    </label>
                  </div>
                  <Select
                    options={tipo_persona.map((item) => ({
                      value: item.id,
                      label: item.name,
                    }))}
                    placeholder="Seleccione"
                    onChange={(option: any) => {
                      const value = option ? Number(option.value) : 0;
                      setSelectedRegimen(option);
                      formEmpresa.setFieldValue('type_contributor_id', value);
                      formEmpresa.setFieldValue('name', '');
                      formEmpresa.setFieldValue('business_name', '');
                      formEmpresa.setFieldValue('commission_percent', '');
                      formEmpresa.setFieldValue('street', '');
                      formEmpresa.setFieldValue('city', '');
                      formEmpresa.setFieldValue('state', '');
                      formEmpresa.setFieldValue('type_commission_id', 0);
                      formEmpresa.setFieldValue('postal_code', '');
                      setSelectedComision(null);
                    }}
                    value={selectedRegimen}
                    isClearable
                    noOptionsMessage={() => <span>No hay registros</span>}
                    styles={{
                      placeholder: (provided) => ({
                        ...provided,
                        color: '#181c32',
                      }),
                      indicatorSeparator: (provided) => ({
                        ...provided,
                        display: 'none',
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        color: '#7E8199',
                      }),
                      control: (provided, state) => ({
                        ...provided,
                        border: '1px solid #e4e6ef',
                        borderRadius: '6px',
                        padding: '3px',
                        fontSize: '1.1rem',
                        boxShadow: 'none',
                        backgroundColor: state.isDisabled ? '#EFF2F5' : 'white',
                      }),
                      option: (provided) => ({
                        ...provided,
                        fontSize: '1.1rem',
                      }),
                    }}
                  />
                  {formEmpresa.touched.type_contributor_id &&
                    formEmpresa.errors.type_contributor_id && (
                      <div className="fv-plugins-message-container text-danger w-auto">
                        <span role="alert">
                          {formEmpresa.errors.type_contributor_id}
                        </span>
                      </div>
                    )}
                </div>
              ) : null}

              {formEmpresa.values.type_contributor_id === 1 && (
                <>
                  <div className="col-md-3">
                    <div className="w-100 d-flex">
                      <label className="required fw-bold fs-6 mb-2 w-auto">
                        Comisión
                      </label>
                    </div>
                    <Select
                      options={tipo_comision.map((item) => ({
                        value: item.id,
                        label: item.name,
                      }))}
                      placeholder="Seleccione"
                      onChange={(option: any) => {
                        const value = option ? Number(option.value) : 0;
                        setSelectedComision(option);
                        formEmpresa.setFieldValue('type_commission_id', value);
                      }}
                      value={selectedComision}
                      isClearable
                      noOptionsMessage={() => <span>No hay registros</span>}
                      styles={{
                        placeholder: (provided) => ({
                          ...provided,
                          color: '#181c32',
                        }),
                        indicatorSeparator: (provided) => ({
                          ...provided,
                          display: 'none',
                        }),
                        dropdownIndicator: (provided) => ({
                          ...provided,
                          color: '#7E8199',
                        }),
                        control: (provided, state) => ({
                          ...provided,
                          border: '1px solid #e4e6ef',
                          borderRadius: '6px',
                          padding: '3px',
                          fontSize: '1.1rem',
                          boxShadow: 'none',
                          backgroundColor: state.isDisabled
                            ? '#EFF2F5'
                            : 'white',
                        }),
                        option: (provided) => ({
                          ...provided,
                          fontSize: '1.1rem',
                        }),
                      }}
                    />
                    {formEmpresa.touched.type_commission_id &&
                      formEmpresa.errors.type_commission_id && (
                        <div className="fv-plugins-message-container text-danger w-auto ms-5">
                          <span role="alert">
                            {formEmpresa.errors.type_commission_id}
                          </span>
                        </div>
                      )}
                  </div>
                  <div className="col-md-3 fv-row fv-plugins-icon-container">
                    <div className="w-100 d-flex">
                      <label className="required fw-bold fs-6 mb-2 w-40">
                        % Comisión
                      </label>
                    </div>
                    <NumericFormat
                      type="text"
                      name="commission_percent"
                      className="form-control text-gray-700 placeholder-gray-400 mw-500px"
                      value={formEmpresa.values.commission_percent}
                      decimalScale={0}
                      allowLeadingZeros
                      fixedDecimalScale
                      placeholder="0"
                      suffix={' %'}
                      thousandSeparator={''}
                      allowNegative={false}
                      isAllowed={(values: any) => {
                        const { floatValue } = values;
                        let allow =
                          floatValue <= 100 || floatValue === undefined;
                        return allow;
                      }}
                      onValueChange={changeUtilityValue}
                    />
                    {formEmpresa.touched.commission_percent &&
                      formEmpresa.errors.commission_percent && (
                        <div className="fv-plugins-message-container text-danger w-60 ms-5">
                          <span role="alert">
                            {formEmpresa.errors.commission_percent}
                          </span>
                        </div>
                      )}
                  </div>
                </>
              )}
              {(formEmpresa.values.type_contributor_id === 2 ||
                formEmpresa.values.type_id === 12) && (
                <>
                  <div className="col-md-3 fv-row fv-plugins-icon-container">
                    <div className="w-100 d-flex">
                      <label className={`fw-bold fs-6 mb-2 w-auto required`}>
                        Nombre de la empresa
                      </label>
                    </div>
                    <input
                      placeholder="Nombre"
                      {...formEmpresa.getFieldProps('name')}
                      className="form-control"
                      type="text"
                      name="name"
                      autoComplete="off"
                      onKeyDown={(e) => {
                        if (
                          !/^[a-zA-Z0-9áéíóúÁÉÍÓÚüÜñÑ\s]$/.test(e.key) &&
                          ![
                            'Backspace',
                            'Tab',
                            'ArrowLeft',
                            'ArrowRight',
                            'Delete',
                          ].includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                    {formEmpresa.touched.name && formEmpresa.errors.name && (
                      <div className="fv-plugins-message-container text-danger w-100 ms-5">
                        <span role="alert">{formEmpresa.errors.name}</span>
                      </div>
                    )}
                  </div>

                  <div className="col-md-3">
                    <div className="w-100 d-flex">
                      <label className="required fw-bold fs-6 mb-2 w-auto">
                        Razón social
                      </label>
                    </div>
                    <input
                      placeholder="Razón social"
                      {...formEmpresa.getFieldProps('business_name')}
                      className="form-control"
                      type="text"
                      name="business_name"
                      autoComplete="off"
                      onKeyDown={(e) => {
                        if (
                          !/^[a-zA-Z0-9áéíóúÁÉÍÓÚüÜñÑ\s]$/.test(e.key) &&
                          ![
                            'Backspace',
                            'Tab',
                            'ArrowLeft',
                            'ArrowRight',
                            'Delete',
                          ].includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                    {formEmpresa.touched.business_name &&
                      formEmpresa.errors.business_name && (
                        <div className="fv-plugins-message-container text-danger w-auto ms-5">
                          <span role="alert">
                            {formEmpresa.errors.business_name}
                          </span>
                        </div>
                      )}
                  </div>
                  <div className="col-md-3">
                    <div className="w-100 d-flex">
                      <label className="required fw-bold fs-6 mb-2 w-auto">
                        Comisión
                      </label>
                    </div>
                    <Select
                      options={tipo_comision.map((item) => ({
                        value: item.id,
                        label: item.name,
                      }))}
                      placeholder="Seleccione"
                      onChange={(option: any) => {
                        const value = option ? Number(option.value) : 0;
                        setSelectedComision(option);
                        formEmpresa.setFieldValue('type_commission_id', value);
                      }}
                      value={selectedComision}
                      isClearable
                      noOptionsMessage={() => <span>No hay registros</span>}
                      styles={{
                        placeholder: (provided) => ({
                          ...provided,
                          color: '#181c32',
                        }),
                        indicatorSeparator: (provided) => ({
                          ...provided,
                          display: 'none',
                        }),
                        dropdownIndicator: (provided) => ({
                          ...provided,
                          color: '#7E8199',
                        }),
                        control: (provided, state) => ({
                          ...provided,
                          border: '1px solid #e4e6ef',
                          borderRadius: '6px',
                          padding: '3px',
                          fontSize: '1.1rem',
                          boxShadow: 'none',
                          backgroundColor: state.isDisabled
                            ? '#EFF2F5'
                            : 'white',
                        }),
                        option: (provided) => ({
                          ...provided,
                          fontSize: '1.1rem',
                        }),
                      }}
                    />
                    {formEmpresa.touched.type_commission_id &&
                      formEmpresa.errors.type_commission_id && (
                        <div className="fv-plugins-message-container text-danger w-auto ms-5">
                          <span role="alert">
                            {formEmpresa.errors.type_commission_id}
                          </span>
                        </div>
                      )}
                  </div>
                  <div className="col-md-3 fv-row fv-plugins-icon-container">
                    <div className="w-100 d-flex">
                      <label className="required fw-bold fs-6 mb-2 w-40">
                        % Comisión
                      </label>
                    </div>
                    <NumericFormat
                      type="text"
                      name="commission_percent"
                      className="form-control text-gray-700 placeholder-gray-400 mw-500px"
                      value={formEmpresa.values.commission_percent}
                      decimalScale={0}
                      allowLeadingZeros
                      fixedDecimalScale
                      placeholder="0"
                      suffix={' %'}
                      thousandSeparator={''}
                      allowNegative={false}
                      isAllowed={(values: any) => {
                        const { floatValue } = values;
                        let allow =
                          floatValue <= 100 || floatValue === undefined;
                        return allow;
                      }}
                      onValueChange={changeUtilityValue}
                    />
                    {formEmpresa.touched.commission_percent &&
                      formEmpresa.errors.commission_percent && (
                        <div className="fv-plugins-message-container text-danger w-60 ms-5">
                          <span role="alert">
                            {formEmpresa.errors.commission_percent}
                          </span>
                        </div>
                      )}
                  </div>

                  <div className="text-gray-700 mt-7 fs-4 fw-bold">
                    Ubicación de la empresa
                  </div>
                  <div className="separator my-5"></div>
                  <div className="col-md-3 fv-row fv-plugins-icon-container">
                    <div className="w-100 d-flex">
                      <label className="required fw-bold fs-6 mb-2 w-40">
                        Código postal
                      </label>
                    </div>
                    <NumericFormat
                      name="postal_code"
                      className="form-control text-gray-700 placeholder-gray-400 mw-500px"
                      value={formEmpresa.values.postal_code}
                      decimalScale={0}
                      allowLeadingZeros
                      maxLength={5}
                      minLength={5}
                      fixedDecimalScale
                      placeholder="Código postal"
                      prefix=""
                      thousandSeparator=""
                      pattern="[0-9]*"
                      onValueChange={(values) => {
                        const { value } = values;
                        formEmpresa.setFieldValue(
                          'postal_code',
                          value ? value : ''
                        );
                      }}
                    />
                    {formEmpresa.touched.postal_code &&
                      formEmpresa.errors.postal_code && (
                        <div className="fv-plugins-message-container text-danger w-60 ms-5">
                          <span role="alert">
                            {formEmpresa.errors.postal_code}
                          </span>
                        </div>
                      )}
                  </div>
                  <div className="col-md-3">
                    <div className="w-100 d-flex">
                      <label className="required fw-bold fs-6 mb-2 w-auto">
                        Estado
                      </label>
                    </div>
                    <input
                      placeholder="Estado"
                      {...formEmpresa.getFieldProps('state')}
                      className="form-control"
                      type="text"
                      name="state"
                      autoComplete="off"
                      onKeyDown={(e) => {
                        if (
                          !/^[a-zA-ZáéíóúÁÉÍÓÚüÜñÑ\s]$/.test(e.key) &&
                          ![
                            'Backspace',
                            'Tab',
                            'ArrowLeft',
                            'ArrowRight',
                            'Delete',
                          ].includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                    {formEmpresa.touched.state && formEmpresa.errors.state && (
                      <div className="fv-plugins-message-container text-danger w-auto ms-5">
                        <span role="alert">{formEmpresa.errors.state}</span>
                      </div>
                    )}
                  </div>
                  <div className="col-md-3">
                    <div className="w-100 d-flex">
                      <label className="required fw-bold fs-6 mb-2 w-auto">
                        Ciudad
                      </label>
                    </div>
                    <input
                      placeholder="Ciudad"
                      {...formEmpresa.getFieldProps('city')}
                      className="form-control"
                      type="text"
                      name="city"
                      autoComplete="off"
                      onKeyDown={(e) => {
                        if (
                          !/^[a-zA-ZáéíóúÁÉÍÓÚüÜñÑ\s]$/.test(e.key) &&
                          ![
                            'Backspace',
                            'Tab',
                            'ArrowLeft',
                            'ArrowRight',
                            'Delete',
                          ].includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                    {formEmpresa.touched.city && formEmpresa.errors.city && (
                      <div className="fv-plugins-message-container text-danger w-auto ms-5">
                        <span role="alert">{formEmpresa.errors.city}</span>
                      </div>
                    )}
                  </div>
                  <div className="col-md-3">
                    <div className="w-100 d-flex">
                      <label className="required fw-bold fs-6 mb-2 w-auto">
                        Calle y Número
                      </label>
                    </div>
                    <input
                      placeholder="Calle y Número"
                      {...formEmpresa.getFieldProps('street')}
                      className="form-control"
                      type="text"
                      name="street"
                      autoComplete="off"
                      onKeyDown={(e) => {
                        if (
                          !/^[a-zA-ZáéíóúÁÉÍÓÚüÜñÑ0-9#\-\_\/\s]$/.test(e.key) &&
                          ![
                            'Backspace',
                            'Tab',
                            'ArrowLeft',
                            'ArrowRight',
                            'Delete',
                          ].includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                    {formEmpresa.touched.street && formEmpresa.errors.street && (
                      <div className="fv-plugins-message-container text-danger w-auto ms-5">
                        <span role="alert">{formEmpresa.errors.street}</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* fin socio */}

            <div className="text-gray-700 mt-7 fs-4 fw-bold">
              Datos del encargado
            </div>
            <div className="separator my-5"></div>
            <div className="row g-9 mb-5">
              <div className="col-md-4 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-auto">
                    Nombre
                  </label>
                </div>
                <input
                  placeholder="Nombre"
                  {...formEmpresa.getFieldProps('first_name')}
                  className="form-control mb-3 mb-lg-0"
                  type="text"
                  name="first_name"
                  autoComplete="off"
                  onChange={(e) => {
                    formEmpresa.setFieldValue('first_name', e.target.value);
                  }}
                  onBlur={(e) => {
                    handleChangeInput(e, 'first_name');
                  }}
                />
                {formEmpresa.touched.first_name &&
                  formEmpresa.errors.first_name && (
                    <div className="fv-plugins-message-container text-danger w-100 ms-5">
                      <span role="alert">{formEmpresa.errors.first_name}</span>
                    </div>
                  )}
              </div>
              <div className="col-md-4 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-40">
                    Apellido Paterno
                  </label>
                </div>
                <input
                  placeholder="Apellido Paterno"
                  {...formEmpresa.getFieldProps('father_last_name')}
                  className="form-control mb-3 mb-lg-0"
                  type="text"
                  name="father_last_name"
                  autoComplete="off"
                  onChange={(e) => {
                    formEmpresa.setFieldValue(
                      'father_last_name',
                      e.target.value
                    );
                  }}
                  onBlur={(e) => {
                    handleChangeInput(e, 'father_last_name');
                  }}
                />
                {formEmpresa.touched.father_last_name &&
                  formEmpresa.errors.father_last_name && (
                    <div className="fv-plugins-message-container text-danger w-50 ms-5">
                      <span role="alert">
                        {formEmpresa.errors.father_last_name}
                      </span>
                    </div>
                  )}
              </div>
              <div className="col-md-4 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="fw-bold fs-6 mb-2 w-40">
                    Apellido Materno
                  </label>
                </div>
                <input
                  placeholder="Apellido Materno"
                  {...formEmpresa.getFieldProps('mother_last_name')}
                  className="form-control mb-3 mb-lg-0"
                  type="text"
                  name="mother_last_name"
                  autoComplete="off"
                  onChange={(e) => {
                    formEmpresa.setFieldValue(
                      'mother_last_name',
                      e.target.value
                    );
                  }}
                  onBlur={(e) => {
                    handleChangeInput(e, 'mother_last_name');
                  }}
                />
                {formEmpresa.touched.mother_last_name &&
                  formEmpresa.errors.mother_last_name && (
                    <div className="fv-plugins-message-container text-danger w-60 ms-5">
                      <span role="alert">
                        {formEmpresa.errors.mother_last_name}
                      </span>
                    </div>
                  )}
              </div>
            </div>
            <div className="row g-9 mb-5">
              <div className="col-md-3 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-auto">
                    Género
                  </label>
                </div>
                <Select
                  options={genders.map((item) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                  placeholder="Seleccione"
                  onChange={(option: any) => {
                    const value = option ? Number(option.value) : 0;
                    setSelectedGender(option);
                    formEmpresa.setFieldValue('gender_id', value);
                  }}
                  value={selectedGender}
                  isClearable
                  noOptionsMessage={() => <span>No hay registros</span>}
                  styles={{
                    placeholder: (provided) => ({
                      ...provided,
                      color: '#181c32',
                    }),
                    indicatorSeparator: (provided) => ({
                      ...provided,
                      display: 'none',
                    }),
                    dropdownIndicator: (provided) => ({
                      ...provided,
                      color: '#7E8199',
                    }),
                    control: (provided, state) => ({
                      ...provided,
                      border: '1px solid #e4e6ef',
                      borderRadius: '6px',
                      padding: '3px',
                      fontSize: '1.1rem',
                      boxShadow: 'none',
                      backgroundColor: state.isDisabled ? '#EFF2F5' : 'white',
                    }),
                    option: (provided) => ({
                      ...provided,
                      fontSize: '1.1rem',
                    }),
                  }}
                />
                {formEmpresa.touched.gender_id && formEmpresa.errors.gender_id && (
                  <div className="fv-plugins-message-container text-danger w-auto ms-5">
                    <span role="alert">{formEmpresa.errors.gender_id}</span>
                  </div>
                )}
              </div>
              <div className="col-md-3 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-auto">
                    Fecha de nacimiento
                  </label>
                </div>
                <input
                  type="date"
                  className="form-control"
                  // onKeyDown={(e) => e.preventDefault()}
                  max={maxDate}
                  onChange={(e) => {
                    if (e.target.value) {
                      const fecha = new Date(e.target.value)
                        .toISOString()
                        .split('T')[0];
                      formEmpresa.setFieldValue('birthdate', fecha);
                    }
                  }}
                ></input>
                {formEmpresa.touched.birthdate && formEmpresa.errors.birthdate && (
                  <div className="fv-plugins-message-container text-danger w-auto ms-5">
                    <span role="alert">{formEmpresa.errors.birthdate}</span>
                  </div>
                )}
              </div>
              <div className="col-md-3 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-auto">
                    Correo
                  </label>
                </div>
                <input
                  placeholder="ej.usuario@example.com"
                  {...formEmpresa.getFieldProps('email')}
                  className="form-control mb-3 mb-lg-0"
                  type="text"
                  name="email"
                  autoComplete="off"
                />
                {formEmpresa.touched.email && formEmpresa.errors.email && (
                  <div className="fv-plugins-message-container text-danger w-auto ms-5">
                    <span role="alert">{formEmpresa.errors.email}</span>
                  </div>
                )}
              </div>
              <div className="col-md-3 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-auto">
                    Teléfono
                  </label>
                </div>
                <input
                  className="form-control mb-3"
                  value={formEmpresa.values.phone}
                  placeholder="Teléfono"
                  prefix=""
                  name="phone"
                  onChange={handlePhoneChange}
                />
                {formEmpresa.touched.phone && formEmpresa.errors.phone && (
                  <div className="fv-plugins-message-container text-danger w-auto ms-5">
                    <span role="alert">{formEmpresa.errors.phone}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="text-gray-700 mt-7 fs-4 fw-bold">
              Datos bancarios
            </div>
            <div className="separator my-5"></div>
            <div className="row g-9 mb-5">
              <div className="col-md-3 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-auto">
                    Nombre
                  </label>
                </div>
                <input
                  placeholder="Nombre"
                  {...formEmpresa.getFieldProps('account_owner_first_name')}
                  className="form-control mb-3 mb-lg-0"
                  type="text"
                  name="account_owner_first_name"
                  autoComplete="off"
                  onChange={(e) => {
                    formEmpresa.setFieldValue(
                      'account_owner_first_name',
                      e.target.value
                    );
                  }}
                  onBlur={(e) => {
                    handleChangeInput(e, 'account_owner_first_name');
                  }}
                />
                {formEmpresa.touched.account_owner_first_name &&
                  formEmpresa.errors.account_owner_first_name && (
                    <div className="fv-plugins-message-container text-danger w-100 ms-5">
                      <span role="alert">
                        {formEmpresa.errors.account_owner_first_name}
                      </span>
                    </div>
                  )}
              </div>
              <div className="col-md-3 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-40">
                    Apellido Paterno
                  </label>
                </div>
                <input
                  placeholder="Apellido Paterno"
                  {...formEmpresa.getFieldProps('account_owner_last_name')}
                  className="form-control mb-3 mb-lg-0"
                  type="text"
                  name="account_owner_last_name"
                  autoComplete="off"
                  onChange={(e) => {
                    formEmpresa.setFieldValue(
                      'account_owner_last_name',
                      e.target.value
                    );
                  }}
                  onBlur={(e) => {
                    handleChangeInput(e, 'account_owner_last_name');
                  }}
                />
                {formEmpresa.touched.account_owner_last_name &&
                  formEmpresa.errors.account_owner_last_name && (
                    <div className="fv-plugins-message-container text-danger w-50 ms-5">
                      <span role="alert">
                        {formEmpresa.errors.account_owner_last_name}
                      </span>
                    </div>
                  )}
              </div>
              <div className="col-md-3 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className=" fw-bold fs-6 mb-2 w-auto required">
                    Banco
                  </label>
                </div>
                <input
                  placeholder="Banco"
                  {...formEmpresa.getFieldProps('bank_name')}
                  className="form-control mb-3 mb-lg-0"
                  type="text"
                  name="bank_name"
                  autoComplete="off"
                  onChange={(e) => {
                    formEmpresa.setFieldValue('bank_name', e.target.value);
                  }}
                  onBlur={(e) => {
                    handleChangeInput(e, 'bank_name');
                  }}
                />
                {formEmpresa.touched.bank_name && formEmpresa.errors.bank_name && (
                  <div className="fv-plugins-message-container text-danger w-auto ms-5">
                    <span role="alert">{formEmpresa.errors.bank_name}</span>
                  </div>
                )}
              </div>
              <div className="col-md-3 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-40">
                    CLABE Interbancaria
                  </label>
                </div>
                <NumericFormat
                  name="interbank_key"
                  className="form-control text-gray-700 placeholder-gray-400 mw-500px"
                  value={formEmpresa.values.interbank_key}
                  decimalScale={0}
                  allowLeadingZeros
                  valueIsNumericString
                  maxLength={18}
                  fixedDecimalScale
                  placeholder="CLABE interbancaria"
                  prefix=""
                  thousandSeparator=""
                  onValueChange={(values) => {
                    const { value } = values;
                    formEmpresa.setFieldValue(
                      'interbank_key',
                      value ? value : ''
                    );
                  }}
                />
                {formEmpresa.touched.interbank_key &&
                  formEmpresa.errors.interbank_key && (
                    <div className="fv-plugins-message-container text-danger w-60 ms-5">
                      <span role="alert">
                        {formEmpresa.errors.interbank_key}
                      </span>
                    </div>
                  )}
              </div>
            </div>

            <div className="text-gray-700 mt-7 fs-4 fw-bold">
              Datos de facturación
            </div>
            <div className="separator my-5"></div>
            <div className="row g-9 mb-5">
              <div className="col-md-4 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-auto">
                    Uso de CFDI
                  </label>
                </div>
                <Select
                  options={cfdi.map((item) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                  placeholder="Seleccione"
                  onChange={(option: any) => {
                    const value = option ? Number(option.value) : 0;
                    setSelectedCfdi(option);
                    formEmpresa.setFieldValue('cfdi_id', value);
                  }}
                  value={selectedCfdi}
                  isClearable
                  noOptionsMessage={() => <span>No hay registros</span>}
                  styles={{
                    placeholder: (provided) => ({
                      ...provided,
                      color: '#181c32',
                    }),
                    indicatorSeparator: (provided) => ({
                      ...provided,
                      display: 'none',
                    }),
                    dropdownIndicator: (provided) => ({
                      ...provided,
                      color: '#7E8199',
                    }),
                    control: (provided, state) => ({
                      ...provided,
                      border: '1px solid #e4e6ef',
                      borderRadius: '6px',
                      padding: '3px',
                      fontSize: '1.1rem',
                      boxShadow: 'none',
                      backgroundColor: state.isDisabled ? '#EFF2F5' : 'white',
                    }),
                    option: (provided) => ({
                      ...provided,
                      fontSize: '1.1rem',
                    }),
                  }}
                />
                {formEmpresa.touched.cfdi_id && formEmpresa.errors.cfdi_id && (
                  <div className="fv-plugins-message-container text-danger w-100 ms-5">
                    <span role="alert">{formEmpresa.errors.cfdi_id}</span>
                  </div>
                )}
              </div>
              <div className="col-md-4 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-40">
                    Correo
                  </label>
                </div>
                <input
                  placeholder="ej.usuario@example.com"
                  {...formEmpresa.getFieldProps('billing_mail')}
                  className="form-control mb-3 mb-lg-0"
                  type="text"
                  name="billing_mail"
                  autoComplete="off"
                />
                {formEmpresa.touched.billing_mail &&
                  formEmpresa.errors.billing_mail && (
                    <div className="fv-plugins-message-container text-danger w-60 ms-5">
                      <span role="alert">
                        {formEmpresa.errors.billing_mail}
                      </span>
                    </div>
                  )}
              </div>
              <div className="col-md-4 fv-row fv-plugins-icon-container">
                <div className="w-100 d-flex">
                  <label className=" fw-bold fs-6 mb-2 w-auto required">
                    RFC
                  </label>
                </div>
                <input
                  placeholder="ej.ABCD00000065T"
                  className="form-control mb-3 mb-lg-0"
                  maxLength={13}
                  type="text"
                  name="rfc"
                  autoComplete="off"
                  value={formEmpresa.values.rfc}
                  onChange={(e) => {
                    const upper = e.target.value.toUpperCase();
                    formEmpresa.setFieldValue('rfc', upper);
                  }}
                />

                {formEmpresa.touched.rfc && formEmpresa.errors.rfc && (
                  <div className="fv-plugins-message-container text-danger w-auto ms-5">
                    <span role="alert">{formEmpresa.errors.rfc}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="row g-9">
              <div className="col-md-12 fv-row fv-plugins-icon-container">
                <div className="d-flex justify-content-center text-center p-2">
                  <a
                    href="#/"
                    className="text-gray-800 text-hover-primary d-flex flex-column"
                  >
                    <div className="symbol symbol-50px mb-6">
                      <img
                        src={
                          formEmpresa.values.pdfFile
                            ? toAbsoluteUrl('/media/svg/files/pdf.svg')
                            : toAbsoluteUrl('/media/svg/files/upload.svg')
                        }
                        className="image-input-wrapper"
                        onClick={onOpenFile}
                        crossOrigin="anonymous"
                        alt="INE"
                      />
                    </div>

                    <div className="d-flex">
                      <div
                        className="required fs-5 mb-2 flex-column"
                        onClick={onOpenFile}
                      >
                        Constancia de situación fiscal
                      </div>
                      <div className="fs-5 mb-2 flex-column ms-5">
                        {formEmpresa.touched.pdfFile &&
                          formEmpresa.errors.pdfFile && (
                            <div className="fv-plugins-message-container text-danger w-100">
                              <span role="alert">
                                {formEmpresa.errors.pdfFile}
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                    <label className="min-h-30px text-success">
                      {documentName}
                    </label>

                    <input
                      style={{ display: 'none' }}
                      ref={constancyRef}
                      type="file"
                      name="pdfFile"
                      accept=".pdf"
                      onChange={(event) => {
                        if (event.currentTarget.files) {
                          let files = event.currentTarget.files;
                          if (files.length > 0) {
                            const file = files[0];
                            const maxSize = 5 * 1024 * 1024;
                            if (file.size > maxSize) {
                              SimpleAlert(
                                'El archivo supera el límite de 5MB.',
                                3000,
                                'error'
                              );
                              event.target.value = '';
                              formEmpresa.setFieldValue('pdfFile', null);
                              setDocumentName('');
                              return;
                            }
                            formEmpresa.setFieldValue('pdfFile', file);
                            setDocumentName(file.name);
                          }
                        }
                      }}
                    />
                  </a>
                </div>
              </div>
            </div>

            <div className="text-center pt-10">
              <button
                type="reset"
                className="btn btn-light me-3"
                data-kt-users-modal-action="cancel"
                onClick={() => {
                  resetFormState();
                  handleClose(false);
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                data-kt-users-modal-action="submit"
                disabled={loading}
              >
                {!loading && <span className="indicator-label">Guardar</span>}
                {loading && (
                  <span
                    className="indicator-progress"
                    style={{ display: 'block' }}
                  >
                    Espere por favor...
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};
export { CreateModal };
